import type { InvoiceDetail } from "@dishour/shared";
import { formatDateTime, formatMoney, formatQtyExact } from "../lib/format.js";

/** The screen and print use the same markup — so the printed version never drifts on its own. */
export function InvoiceDocument({ invoice }: { invoice: InvoiceDetail }) {
  return (
    <article className="invoice-document rounded-xl border border-stone-200 bg-white p-6">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold">청구서</h1>
          <p className="text-sm text-stone-500">No. {invoice.number}</p>
          <p className="text-sm text-stone-500">발행 {formatDateTime(invoice.issuedAt)}</p>
        </div>
        <div className="text-right">
          <p className="font-semibold">{invoice.supplier.name}</p>
          {invoice.supplierContact.contactName ? (
            <p className="text-sm text-stone-600">{invoice.supplierContact.contactName}</p>
          ) : null}
          {invoice.supplierContact.phone ? (
            <p className="text-sm text-stone-600">{invoice.supplierContact.phone}</p>
          ) : null}
        </div>
      </header>

      <section className="mb-4">
        <p className="text-sm text-stone-500">받는 곳</p>
        <p className="font-semibold">{invoice.restaurant.name}</p>
        {invoice.restaurantContact.phone ? (
          <p className="text-sm text-stone-600">{invoice.restaurantContact.phone}</p>
        ) : null}
      </section>

      <table className="mb-4 w-full text-sm">
        <thead>
          <tr className="border-b border-stone-300 text-left">
            <th className="py-2 font-medium">품목</th>
            <th className="py-2 text-right font-medium">주문</th>
            <th className="py-2 text-right font-medium">실납품</th>
            <th className="py-2 text-right font-medium">단가</th>
            <th className="py-2 text-right font-medium">금액</th>
          </tr>
        </thead>
        <tbody>
          {invoice.lines.map((line) => {
            // Lines are immutable snapshots (Global Constraints) — is_divisible is never re-joined
            // from the current SKU. The snapshotted quantity string already carries that
            // information: a divisible quantity was recorded with a decimal point.
            const unitInfo = { unit: line.unit, isDivisible: line.fulfilledQty.includes(".") };
            return (
              <tr key={line.lineNo} className="border-b border-stone-100">
                <td className="py-2">{line.description}</td>
                <td className="py-2 text-right tabular-nums">{formatQtyExact(line.orderedQty, unitInfo)}</td>
                <td className="py-2 text-right font-medium tabular-nums">
                  {formatQtyExact(line.fulfilledQty, unitInfo)}
                </td>
                <td className="py-2 text-right tabular-nums">{formatMoney(line.unitPrice)}</td>
                <td className="py-2 text-right tabular-nums">{formatMoney(line.amount)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="flex justify-end">
        <p className="text-lg font-bold">합계 {formatMoney(invoice.total)}</p>
      </div>

      {invoice.note ? <p className="mt-4 text-sm text-stone-600">{invoice.note}</p> : null}

      <p className="mt-6 text-xs text-stone-400">
        대금 수수는 공급업체와 식당이 직접 처리합니다. Dishour는 결제를 대행하지 않습니다.
      </p>
      {invoice.paidAt ? (
        <p className="mt-1 text-sm font-medium text-emerald-700">
          수금 완료 표시됨 · {formatDateTime(invoice.paidAt)}
        </p>
      ) : null}
    </article>
  );
}
